import { AddIcon, ChevronRightIcon } from "@chakra-ui/icons";
import {
	Badge,
	Breadcrumb,
	BreadcrumbItem,
	BreadcrumbLink,
	Flex,
	Input,
	Text,
	useDisclosure,
} from "@chakra-ui/react";
import {
	type ColumnDef,
	getCoreRowModel,
	getFilteredRowModel,
	getPaginationRowModel,
	getSortedRowModel,
	useReactTable,
} from "@tanstack/react-table";
import { useCallback, useMemo, useState } from "react";
import { useMutation, useQueryClient } from "react-query";
import { useNavigate } from "react-router-dom";
import PenIcon from "../components/icons/PenIcon";
import CreateLabelModal from "../components/modals/CreateLabelModal";
import PageTitle from "../components/PageTitle";
import PrimaryButton from "../components/PrimaryButton";
import ClientSideTable from "../components/table/ClientSideTable";
import { useTicketLabels } from "../hooks/ticket_labels.hooks";
import { useConnectedUser } from "../hooks/user.hooks";
import { deleteTicketLabel } from "../mutations/ticket_label.mutations";
import {
	type TicketLabel,
	TicketPriority,
	TicketScope,
} from "../types/ticket.types";
import { Pen, Trash2 } from "lucide-react";

function TicketingCreateLabelsPage() {
	const user = useConnectedUser();
	const navigate = useNavigate();
	const { isOpen, onOpen, onClose } = useDisclosure();
	const [search, setSearch] = useState("");
	const [labelSelected, setLabelSelected] = useState<TicketLabel | null>(null);
	const queryClient = useQueryClient();
	const { mutate: mutateDeleteTicketLabel } = useMutation(deleteTicketLabel, {
		onSuccess: () => {
			queryClient.invalidateQueries("ticket_labels");
		},
	});
	const columns = useMemo<ColumnDef<TicketLabel>[]>(
		() => [
			{
				accessorFn: (row) => row.label,
				id: "name",
				header: "Nom du motif",
				cell: (info) => info.getValue(),
				sortDescFirst: true,
			},
			{
				accessorFn: (row) => row.scope,
				id: "scope",
				header: "Utilisateurs",
				cell: ({ row }) => {
					switch (row.original.scope) {
						case TicketScope.SUP:
							return (
								<Badge
									h="1.5rem"
									lineHeight="1.5rem"
									px="1rem"
									rounded="xl"
									border={"solid 1px"}
									borderColor={"#BCD9FF"}
									color={"#1444E1"}
									textTransform={"capitalize"}
									w="fit-content"
									backgroundColor={"#EEF5FF"}
								>
									Supembouts
								</Badge>
							);
						case TicketScope.CLIENTS:
							return (
								<Badge
									h="1.5rem"
									lineHeight="1.5rem"
									px="1rem"
									rounded="xl"
									border={"solid 1px"}
									borderColor={"#E9D5FF"}
									color={"#7E22CE"}
									textTransform={"capitalize"}
									w="fit-content"
									backgroundColor={"#FAF5FF"}
								>
									Audio
								</Badge>
							);
						case TicketScope.ALL:
							return (
								<Badge
									h="1.5rem"
									lineHeight="1.5rem"
									px="1rem"
									rounded="xl"
									border={"solid 1px"}
									borderColor={"#F1F5F9"}
									color={"#334155"}
									textTransform={"capitalize"}
									w="fit-content"
									backgroundColor={"#F8FAFC"}
								>
									Tout le monde
								</Badge>
							);
						default:
							return row.original.scope;
					}
				},
				sortDescFirst: true,
				meta: {
					headerClassName: "flex justify-center!",
					rowClassName: "text-center",
				},
			},
			{
				accessorFn: (row) => row.proof,
				id: "prouf",
				header: "Preuve",
				cell: ({ row }) => (
					<Badge
						h="1.5rem"
						lineHeight="1.5rem"
						px="1rem"
						rounded="md"
						border={"solid 1px"}
						borderColor={"gray.100"}
						color={"gray.700"}
						textTransform={"capitalize"}
						w="fit-content"
						backgroundColor={"white"}
					>
						{row.original.proof ? "Oui" : "Non"}
					</Badge>
				),
				sortDescFirst: true,
				meta: {
					headerClassName: "flex justify-center!",
					rowClassName: "text-center",
				},
			},
			{
				accessorFn: (row) => row.proof,
				id: "prouf",
				header: "Publié",
				cell: ({ row }) => (
					<Badge
						h="1.5rem"
						lineHeight="1.5rem"
						px="1rem"
						rounded="md"
						border={"solid 1px"}
						borderColor={"gray.100"}
						color={"gray.700"}
						textTransform={"capitalize"}
						w="fit-content"
						backgroundColor={"white"}
					>
						{row.original.published ? "Oui" : "Non"}
					</Badge>
				),
				sortDescFirst: true,
				meta: {
					headerClassName: "flex justify-center!",
					rowClassName: "text-center",
				},
			},
			{
				accessorFn: (row) => row.priority,
				id: "priority",
				header: "Priorité",
				cell: ({ row }) => {
					switch (row.original.priority) {
						case TicketPriority.LOW:
							return (
								<Badge
									h="1.5rem"
									lineHeight="1.5rem"
									px="1rem"
									rounded="md"
									border={"solid 1px"}
									borderColor={"green.100"}
									color={"green.700"}
									textTransform={"capitalize"}
									w="fit-content"
									backgroundColor={"green.50"}
								>
									Normale
								</Badge>
							);
						case TicketPriority.MEDIUM:
							return (
								<Badge
									h="1.5rem"
									lineHeight="1.5rem"
									px="1rem"
									rounded="md"
									border={"solid 1px"}
									borderColor={"orange.100"}
									color={"orange.700"}
									textTransform={"capitalize"}
									w="fit-content"
									backgroundColor={"orange.50"}
								>
									Moyenne
								</Badge>
							);
						case TicketPriority.HIGH:
							return (
								<Badge
									h="1.5rem"
									lineHeight="1.5rem"
									px="1rem"
									rounded="md"
									border={"solid 1px"}
									borderColor={"red.100"}
									color={"red.700"}
									textTransform={"capitalize"}
									w="fit-content"
									backgroundColor={"red.50"}
								>
									Haute
								</Badge>
							);
						default:
							return row.original.priority;
					}
				},
				sortDescFirst: true,
				meta: {
					headerClassName: "flex justify-center!",
					rowClassName: "text-center",
				},
			},

			{
				accessorFn: () => "",
				id: "actions",
				header: "",
				cell: ({ row }) => (
					<div className="hflex gap-1">
						<Trash2
							className="text-gray-500 w-5 h-5"
							onClick={() => mutateDeleteTicketLabel(row.original.id)}
						></Trash2>
						<Pen
							className="text-gray-500 w-5 h-5"
							onClick={() => {
								setLabelSelected(row.original);
								onOpen();
							}}
						></Pen>
					</div>
				),
				sortDescFirst: true,
				meta: {
					rowClassName: "w-fit",
				},
			},
		],
		[onOpen, mutateDeleteTicketLabel],
	);
	const { data: rawData } = useTicketLabels({ all: true });
	const data = useMemo(() => rawData ?? [], [rawData]);
	const table = useReactTable({
		data: data,
		columns: columns,
		getCoreRowModel: getCoreRowModel(),
		getSortedRowModel: getSortedRowModel(),
		getPaginationRowModel: getPaginationRowModel(),
		getFilteredRowModel: getFilteredRowModel(),
		manualPagination: true,
		rowCount: data.length,
	});
	const handleOnNew = useCallback(() => {
		onOpen();
	}, [onOpen]);
	const handleOnClose = useCallback(() => {
		onClose();
		setLabelSelected(null);
	}, [onClose]);
	const TicketingCreateLabelsPageNoDataComponent = () => {
		return (
			<Flex w="100%" justifyContent="center">
				<Flex
					w="100%"
					rounded="xl"
					bg="white"
					textAlign="center"
					lineHeight="44px"
					color="gray.500"
					direction={"column"}
					justifyContent={"center"}
					alignItems={"center"}
					py="100px"
				>
					<Flex
						w="48px"
						h="48px"
						bg="zinc.50"
						border="solid 1px"
						borderColor="brand.100"
						justifyContent="center"
						alignItems="center"
						borderRadius={100}
						mr="8px"
					>
						<PenIcon w="24px" h="24px" color="brand.700"></PenIcon>
					</Flex>
					<Text
						mt={4}
						color={"gray.900"}
						fontSize={"20px"}
						lineHeight={"30px"}
						fontWeight={600}
					>
						Aucun labels
					</Text>
					<PrimaryButton mt={4} mb={4} w={"30rem"} onClick={handleOnNew}>
						Créer un label
					</PrimaryButton>
				</Flex>
			</Flex>
		);
	};
	const TicketingCreateLabelsPageActionsComponent = () => {
		return (
			<>
				<PrimaryButton w={"fit-content"} onClick={handleOnNew}>
					<AddIcon mr={4}></AddIcon>
					Ajouter un libellé
				</PrimaryButton>
			</>
		);
	};
	if (!user?.isSuperAdmin) {
		navigate(-1);
		return;
	}
	return (
		<>
			<Breadcrumb
				spacing="8px"
				marginBottom={"20px"}
				separator={<ChevronRightIcon color="gray.500" />}
			>
				<BreadcrumbItem>
					<BreadcrumbLink
						href="/tickets"
						color="gray.600"
						fontWeight={600}
						fontSize="14px"
					>
						Ticketing
					</BreadcrumbLink>
				</BreadcrumbItem>

				<BreadcrumbItem>
					<BreadcrumbLink
						href="#"
						color="brand.700"
						fontWeight={600}
						fontSize="14px"
					>
						Gérer les libellés
					</BreadcrumbLink>
				</BreadcrumbItem>
			</Breadcrumb>

			<Flex
				alignItems={"center"}
				justifyContent={"space-between"}
				marginBottom={"32px"}
			>
				<PageTitle>Gérer les libellés</PageTitle>

				<Flex alignItems={"center"} gap={"16px"}>
					<Input
						value={search}
						onChange={(evt) => setSearch(String(evt.target.value))}
						placeholder="Rechercher"
						w="20rem"
					/>
					<TicketingCreateLabelsPageActionsComponent />
				</Flex>
			</Flex>

			<ClientSideTable
				table={table}
				w={["auto", "10rem", "10rem", "200px", "80px"]}
				flexGrow={[1, 0, 0, 0, 0]}
				noDataComponent={TicketingCreateLabelsPageNoDataComponent}
				noSearch
				noPagination
				search={search}
				setSearch={setSearch}
			></ClientSideTable>

			<CreateLabelModal
				isOpen={isOpen}
				onOpen={onOpen}
				onClose={handleOnClose}
				defaultValue={labelSelected}
			></CreateLabelModal>
		</>
	);
}

export default TicketingCreateLabelsPage;
